import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import VendorSyncCard from "@/components/VendorSyncCard";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATS = ["all", "breaker", "contactor", "vfd", "transformer", "enclosure", "relay", "plc", "heat_trace_controller"];

export default function Library() {
  const [manufacturers, setManufacturers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [mfg, setMfg] = useState("all");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/manufacturers");
        setManufacturers(data);
      } catch (err) {
        console.warn("Failed to load manufacturers:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (mfg !== "all") params.manufacturer = mfg;
        if (cat !== "all") params.category = cat;
        if (q) params.q = q;
        const { data } = await api.get("/equipment", { params });
        setEquipment(data);
      } catch (err) {
        console.error("Failed to fetch equipment:", err);
        toast.error("Failed to fetch equipment");
      }
    })();
  }, [mfg, cat, q]);

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="library" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// Library</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Manufacturer Library</h1>
        <p className="text-gray-400 mt-2">Browse representative parts from leading manufacturers.</p>

        {/* Filters */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          <div className="bg-[#12151C] p-4">
            <div className="label-tag mb-2">Manufacturer</div>
            <select
              value={mfg}
              onChange={(e) => setMfg(e.target.value)}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
              data-testid="filter-manufacturer"
            >
              <option value="all">All</option>
              {manufacturers.map((m) => <option key={m.slug} value={m.slug}>{m.name}</option>)}
            </select>
          </div>
          <div className="bg-[#12151C] p-4">
            <div className="label-tag mb-2">Category</div>
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
              data-testid="filter-category"
            >
              {CATS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="bg-[#12151C] p-4 md:col-span-2">
            <div className="label-tag mb-2">Search part #/desc</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. NSX, 100A, contactor"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
              data-testid="filter-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 border border-white/10 overflow-x-auto">
          <table className="w-full" data-testid="equipment-table">
            <thead className="bg-[#1A1F29] text-left">
              <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono">
                <th className="p-3">Part #</th>
                <th className="p-3">Manufacturer</th>
                <th className="p-3">Category</th>
                <th className="p-3">Description</th>
                <th className="p-3">A</th>
                <th className="p-3">P</th>
                <th className="p-3">V</th>
                <th className="p-3 text-right">USD</th>
              </tr>
            </thead>
            <tbody>
              {equipment.map((e) => (
                <tr key={`${e.manufacturer}-${e.part_number}`} className="border-t border-white/5 hover:bg-white/[0.03] font-mono text-sm">
                  <td className="p-3 text-[#00E5FF]">{e.part_number}</td>
                  <td className="p-3 capitalize">{e.manufacturer}</td>
                  <td className="p-3 text-gray-400 capitalize">{e.category.replace(/_/g, " ")}</td>
                  <td className="p-3 text-gray-300">{e.description}</td>
                  <td className="p-3">{e.amps || "—"}</td>
                  <td className="p-3">{e.poles || "—"}</td>
                  <td className="p-3">{e.voltage || "—"}</td>
                  <td className="p-3 text-right">${e.price.toFixed(2)}</td>
                </tr>
              ))}
              {equipment.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-gray-500">No parts found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* v2.56 — Admin vendor sync (Schneider first). Card auto-hides
            for non-admins via a silent 403 from /api/vendors. */}
        <VendorSyncCard />
      </div>
    </div>
  );
}
